import { useState, useEffect } from 'react';
import API from '@/lib/api';
import { Plus, Trash2, ChefHat } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

const CATEGORIES = ['appetizer', 'main', 'dessert', 'side', 'breakfast', 'lunch', 'dinner'];

export default function ChefMenus() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', category: 'main', ingredients: '', price: '' });

  const fetch = () => { API.get('/menu-items').then(r => setItems(r.data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(fetch, []);

  const handleCreate = async () => {
    try {
      await API.post('/menu-items', { ...form, ingredients: form.ingredients.split(',').map(s => s.trim()).filter(Boolean), price: Number(form.price) });
      setOpen(false);
      setForm({ name: '', description: '', category: 'main', ingredients: '', price: '' });
      fetch();
    } catch { /* ignore */ }
  };

  const handleDelete = async (id) => {
    await API.delete(`/menu-items/${id}`);
    fetch();
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#E05A47] border-t-transparent rounded-full animate-spin" /></div>;

  const grouped = CATEGORIES.reduce((acc, cat) => {
    const catItems = items.filter(i => i.category === cat);
    if (catItems.length > 0) acc[cat] = catItems;
    return acc;
  }, {});

  return (
    <div className="fade-in space-y-6" data-testid="chef-menus-page">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-extrabold tracking-tight text-[#222] font-[Manrope]">Menu Management</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#E05A47] hover:bg-[#C54A39] text-white rounded-lg font-semibold gap-1.5 btn-press" data-testid="add-menu-item-btn">
              <Plus className="w-4 h-4" /> Add Item
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle className="font-[Manrope]">Add Menu Item</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-2">
              <div><Label className="text-sm">Name</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="mt-1 rounded-lg border-[#EBEBEB] h-10" data-testid="menu-name-input" /></div>
              <div><Label className="text-sm">Description</Label><textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} className="mt-1 w-full rounded-lg border border-[#EBEBEB] px-3 py-2 text-sm" data-testid="menu-desc-input" /></div>
              <div><Label className="text-sm">Category</Label>
                <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="mt-1 w-full rounded-lg border border-[#EBEBEB] px-3 py-2 text-sm h-10" data-testid="menu-category-select">
                  {CATEGORIES.map(c => <option key={c} value={c} className="capitalize">{c}</option>)}
                </select>
              </div>
              <div><Label className="text-sm">Ingredients (comma-separated)</Label><Input value={form.ingredients} onChange={e => setForm(f => ({ ...f, ingredients: e.target.value }))} className="mt-1 rounded-lg border-[#EBEBEB] h-10" data-testid="menu-ingredients-input" /></div>
              <div><Label className="text-sm">Price ($)</Label><Input type="number" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} className="mt-1 rounded-lg border-[#EBEBEB] h-10" data-testid="menu-price-input" /></div>
              <Button onClick={handleCreate} className="w-full bg-[#E05A47] hover:bg-[#C54A39] text-white rounded-lg font-semibold btn-press" data-testid="save-menu-item-btn">Add to Menu</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-16 text-[#717171]">No menu items yet. Add your signature dishes!</div>
      ) : (
        Object.entries(grouped).map(([cat, catItems]) => (
          <div key={cat}>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-[#717171] mb-3 capitalize">{cat}s</h2>
            <div className="space-y-2">
              {catItems.map(item => (
                <div key={item.id} className="bg-white rounded-xl border border-[#EBEBEB] p-4 flex items-center justify-between" data-testid={`menu-item-${item.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#E05A47]/10 flex items-center justify-center">
                      <ChefHat className="w-5 h-5 text-[#E05A47]" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="font-bold text-sm text-[#222] font-[Manrope]">{item.name}</p>
                      <p className="text-xs text-[#717171]">{item.description}</p>
                      {item.ingredients?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {item.ingredients.map(i => <span key={i} className="px-1.5 py-0.5 bg-[#F7F7F7] rounded text-[10px] text-[#717171]">{i}</span>)}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-bold text-[#222] font-[Manrope]">${item.price}</span>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(item.id)} className="text-red-400 hover:text-red-600" data-testid={`delete-menu-${item.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
